import React, { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * SolarFieldCanvas
 * ----------------
 * A real-time WebGL hero scene: rows of tilted solar panels receding to a
 * golden-hour horizon, an animated sun glow, and floating light motes.
 * Light & airy palette to sit cohesively above the light theme.
 *
 * Robustness:
 *  - Falls back to the parent's CSS gradient if WebGL is unavailable.
 *  - Respects prefers-reduced-motion (renders a single static frame).
 *  - Pauses the render loop when off-screen or the tab is hidden.
 *  - Caps devicePixelRatio and lowers detail on small screens.
 *  - Disposes all GPU resources on unmount.
 */
export default function SolarFieldCanvas({ className = "" }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // ---- Renderer (bail out gracefully if WebGL is unavailable) ----
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: !isMobile,
        alpha: true,
        powerPreference: "high-performance",
      });
    } catch (err) {
      return; // CSS gradient behind the canvas remains visible
    }

    const width = mount.clientWidth || window.innerWidth;
    const height = mount.clientHeight || Math.round(window.innerHeight * 0.9);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    mount.appendChild(renderer.domElement);

    // ---- Scene + atmospheric fog (blends panels into the warm horizon) ----
    const scene = new THREE.Scene();
    const fogColor = new THREE.Color("#F6EEDD");
    scene.fog = new THREE.Fog(fogColor, 16, 64);

    // ---- Camera ----
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 220);
    const baseCamPos = new THREE.Vector3(0, 3.0, 17);
    const lookTarget = new THREE.Vector3(0, 2.6, -18);
    camera.position.copy(baseCamPos);
    camera.lookAt(lookTarget);

    // ---- Lighting (warm key from the sun + soft sky fill) ----
    scene.add(new THREE.HemisphereLight(0xfff4e2, 0xdfecfb, 1.05));
    const sunLight = new THREE.DirectionalLight(0xffd49a, 1.7);
    sunLight.position.set(-7, 9, -22);
    scene.add(sunLight);
    scene.add(new THREE.AmbientLight(0xffffff, 0.32));

    // ---- Ground (subtle warm plane that fogs into the horizon) ----
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(600, 600),
      new THREE.MeshStandardMaterial({ color: 0xefe6d2, roughness: 1, metalness: 0 })
    );
    ground.rotation.x = -Math.PI / 2;
    scene.add(ground);

    // ---- Procedural solar-cell texture (so each module reads as real PV) ----
    const makePanelTexture = () => {
      const c = document.createElement("canvas");
      c.width = c.height = 256;
      const ctx = c.getContext("2d");
      // frame
      ctx.fillStyle = "#cdd6e0";
      ctx.fillRect(0, 0, 256, 256);
      // glass backing
      ctx.fillStyle = "#16263d";
      ctx.fillRect(10, 10, 236, 236);
      // cells
      const n = 6;
      const gap = 4;
      const inner = 236 - gap * (n + 1);
      const cell = inner / n;
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          const x = 10 + gap + i * (cell + gap);
          const y = 10 + gap + j * (cell + gap);
          ctx.fillStyle = "#22406b";
          ctx.fillRect(x, y, cell, cell);
          // busbar hint
          ctx.strokeStyle = "rgba(120,150,200,0.35)";
          ctx.lineWidth = 1;
          ctx.strokeRect(x + 3, y + 3, cell - 6, cell - 6);
        }
      }
      // diagonal sheen
      const g = ctx.createLinearGradient(0, 0, 256, 256);
      g.addColorStop(0, "rgba(255,255,255,0.20)");
      g.addColorStop(0.45, "rgba(255,255,255,0.0)");
      g.addColorStop(1, "rgba(255,255,255,0.06)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 256, 256);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      return tex;
    };
    const panelTex = makePanelTexture();

    // ---- Panel field (instanced for performance) ----
    const COLS = isMobile ? 5 : 7;
    const ROWS = isMobile ? 9 : 13;
    const count = COLS * ROWS;
    const spacingX = 3.05;
    const spacingZ = 3.25;
    const tilt = -0.5; // radians, faces up toward sun + camera
    const panelY = 1.05;

    const panelGeo = new THREE.BoxGeometry(2.2, 0.07, 1.3);
    const panelMat = new THREE.MeshStandardMaterial({
      map: panelTex,
      roughness: 0.32,
      metalness: 0.55,
      emissive: new THREE.Color(0x16314f),
      emissiveIntensity: 0.12,
    });
    const panels = new THREE.InstancedMesh(panelGeo, panelMat, count);

    const postGeo = new THREE.BoxGeometry(0.12, panelY, 0.12);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x9aa3ad, roughness: 0.8, metalness: 0.2 });
    const posts = new THREE.InstancedMesh(postGeo, postMat, count);

    const dummy = new THREE.Object3D();
    let idx = 0;
    const startX = -((COLS - 1) / 2) * spacingX;
    for (let r = 0; r < ROWS; r++) {
      for (let col = 0; col < COLS; col++) {
        const x = startX + col * spacingX;
        const z = 3 - r * spacingZ;
        // panel
        dummy.position.set(x, panelY, z);
        dummy.rotation.set(tilt, 0, 0);
        dummy.updateMatrix();
        panels.setMatrixAt(idx, dummy.matrix);
        // support post
        dummy.position.set(x, panelY / 2, z + 0.1);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        posts.setMatrixAt(idx, dummy.matrix);
        idx++;
      }
    }
    panels.instanceMatrix.needsUpdate = true;
    posts.instanceMatrix.needsUpdate = true;
    scene.add(posts);
    scene.add(panels);

    // ---- Soft radial sprite texture (reused for sun + motes) ----
    const makeGlow = (inner, outer) => {
      const c = document.createElement("canvas");
      c.width = c.height = 128;
      const ctx = c.getContext("2d");
      const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      g.addColorStop(0, inner);
      g.addColorStop(0.4, outer);
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 128, 128);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    };

    // ---- Sun: layered additive glow + bright core ----
    const sunGroup = new THREE.Group();
    // Off-centre (left, near the horizon) so it never sits behind the centred copy
    sunGroup.position.set(-12, 6, -34);
    const sunLayers = [
      { tex: makeGlow("rgba(255,236,190,0.95)", "rgba(255,205,120,0.35)"), scale: 24, opacity: 0.4 },
      { tex: makeGlow("rgba(255,244,214,0.95)", "rgba(255,221,150,0.5)"), scale: 12, opacity: 0.58 },
      { tex: makeGlow("rgba(255,252,240,1)", "rgba(255,238,200,0.7)"), scale: 5, opacity: 0.85 },
    ];
    const sunSprites = sunLayers.map((l) => {
      const m = new THREE.SpriteMaterial({
        map: l.tex,
        transparent: true,
        opacity: l.opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      });
      const s = new THREE.Sprite(m);
      s.scale.set(l.scale, l.scale, 1);
      sunGroup.add(s);
      return { sprite: s, baseScale: l.scale, baseOpacity: l.opacity };
    });
    scene.add(sunGroup);

    // ---- Floating light motes (drifting upward) ----
    const moteCount = isMobile ? 70 : 140;
    const motePositions = new Float32Array(moteCount * 3);
    const moteSpeed = new Float32Array(moteCount);
    const RANGE_X = 44, RANGE_Y = 16, RANGE_Z = 40;
    for (let i = 0; i < moteCount; i++) {
      motePositions[i * 3] = (Math.random() - 0.5) * RANGE_X;
      motePositions[i * 3 + 1] = Math.random() * RANGE_Y;
      motePositions[i * 3 + 2] = -Math.random() * RANGE_Z + 4;
      moteSpeed[i] = 0.15 + Math.random() * 0.35;
    }
    const moteGeo = new THREE.BufferGeometry();
    moteGeo.setAttribute("position", new THREE.BufferAttribute(motePositions, 3));
    const moteMat = new THREE.PointsMaterial({
      size: 0.5,
      map: makeGlow("rgba(255,248,228,0.95)", "rgba(255,226,160,0.3)"),
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      fog: false,
    });
    const motes = new THREE.Points(moteGeo, moteMat);
    scene.add(motes);

    // ---- Mouse parallax (eased) ----
    const pointer = { x: 0, y: 0 };
    const onPointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    };
    if (!prefersReduced) window.addEventListener("pointermove", onPointerMove, { passive: true });

    // ---- Render loop with visibility gating ----
    const clock = new THREE.Clock();
    let rafId = null;
    let inView = true;

    const renderFrame = () => {
      const t = clock.getElapsedTime();

      // eased camera parallax
      const targetX = baseCamPos.x + pointer.x * 1.6;
      const targetY = baseCamPos.y - pointer.y * 0.8;
      camera.position.x += (targetX - camera.position.x) * 0.04;
      camera.position.y += (targetY - camera.position.y) * 0.04;
      camera.lookAt(lookTarget);

      // sun breathing + warm panel shimmer
      const pulse = 1 + Math.sin(t * 0.6) * 0.04;
      sunSprites.forEach((s) => {
        s.sprite.scale.set(s.baseScale * pulse, s.baseScale * pulse, 1);
        s.sprite.material.opacity = s.baseOpacity * (0.92 + Math.sin(t * 0.8) * 0.08);
      });
      panelMat.emissiveIntensity = 0.12 + Math.sin(t * 0.5) * 0.05;

      // drift motes upward, loop around
      const pos = moteGeo.attributes.position.array;
      for (let i = 0; i < moteCount; i++) {
        pos[i * 3 + 1] += moteSpeed[i] * 0.016;
        pos[i * 3] += Math.sin(t * 0.3 + i) * 0.002;
        if (pos[i * 3 + 1] > RANGE_Y) {
          pos[i * 3 + 1] = 0;
          pos[i * 3] = (Math.random() - 0.5) * RANGE_X;
        }
      }
      moteGeo.attributes.position.needsUpdate = true;

      renderer.render(scene, camera);
    };

    const animate = () => {
      renderFrame();
      rafId = requestAnimationFrame(animate);
    };

    const start = () => {
      if (rafId == null && inView && !document.hidden) {
        clock.start();
        animate();
      }
    };
    const stop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    if (prefersReduced) {
      renderFrame(); // single static frame, no loop
    } else {
      start();
    }

    // pause when scrolled out of view
    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (prefersReduced) return;
        inView ? start() : stop();
      },
      { threshold: 0.01 }
    );
    io.observe(mount);

    const onVisibility = () => {
      if (prefersReduced) return;
      document.hidden ? stop() : start();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ---- Resize ----
    const ro = new ResizeObserver(() => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      if (prefersReduced || (!inView && rafId == null)) renderFrame();
    });
    ro.observe(mount);

    // ---- Cleanup ----
    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointermove", onPointerMove);

      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        }
      });
      panelTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className={className} aria-hidden="true" />;
}
